import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { blockDeleteWithMembers, handleHasMembersError } from '../components/RemoveMember';
import api from '../lib/api';
import useConfirmPurchase from '../lib/useConfirmPurchase';
import { Avatar, PageHead, Section, Spinner, ErrorNote, Empty, useAsync, useRefreshOnFocus } from '../components/ui';
import FilesSection from '../components/FilesSection';
import { formatDateUS, formatTime12h } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import AssociationTabs from '../components/AssociationTabs';
import RelatedStrips from '../components/RelatedStrips';
import PriceField from '../components/PriceField';
import StatusPill from '../components/StatusPill';
import { AnnounceToggle } from '../components/AnnounceField';
import PreviewModal from '../components/PreviewModal';
import Conversation, { ConversationSetup, EMPTY_CONVERSATION_SETUP, applyConversationSetup } from '../components/Conversation';
import ColorField, { cssBackground } from '../components/ColorField';
import IconColorField from '../components/IconColorField';
import ProgramHero from '../components/ProgramHero';
import BackCrumb, { useBackTarget, fromHere } from '../components/BackCrumb';

function ReadField({ label, value }) {
  return (
    <div className="field-block">
      <div className="field-label">{label}</div>
      <div className={`field-value${value ? '' : ' muted'}`}>{value || '—'}</div>
    </div>
  );
}

// Module-level so inputs keep focus across keystrokes.
function EditField({ label, value, onChange, type = 'text', textarea, required, placeholder }) {
  return (
    <div className="form-field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      {textarea
        ? <textarea className="textarea" value={value} onChange={onChange} placeholder={placeholder} />
        : <input className="input" type={type} value={value} onChange={onChange} placeholder={placeholder} />}
    </div>
  );
}

const STATES = ['STARTED', 'ONGOING', 'PARKED', 'COMPLETE'];

function stateLabel(s) {
  switch (s) {
    case 'STARTED':  return 'Upcoming';
    case 'ONGOING':  return 'Ongoing';
    case 'COMPLETE': return 'Complete';
    case 'PARKED':   return 'Parked';
    default: return s || '—';
  }
}

/** Build the editable form-state snapshot from a loaded event. */
function toForm(event) {
  const r = event.request || {};
  return {
    requestName: r.name || '',
    requestTopic: r.topic || '',
    requestDescription: r.description || '',
    date: event.date || '',
    beginTime: event.beginTime || '',
    endTime: event.endTime || '',
    state: event.state || 'STARTED',
    location: event.location || '',
    priceCents: event.priceCents || 0,
    isPrivate: !!event.isPrivate,
    coverColor: event.coverColor || '',
    iconColor: event.iconColor || '',
  };
}

/**
 * Event page — two modes (design/022 + inline-edit refactor):
 *   /event/:id         → view-only. Even the owner sees read-only content
 *                        here; a "Manage event →" button routes them to
 *                        the management route below.
 *   /leader/event/:id  → inline editor. Every field is editable in-place;
 *                        a Save button in the header commits changes.
 *                        Delete + Files upload live here too.
 */
export default function EventDetailPage({ manageMode = false, preview = false } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const fromHome = useLocation().state?.from === 'home';
  const { loading, error, data: event, reload, refresh } = useAsync(() => api.getEvent(id), [id]);
  const purchase = useConfirmPurchase(reload);
  // Participants' view only — the owner's editor keeps its unsaved changes.
  useRefreshOnFocus(refresh, !manageMode && !preview);

  // `?created=1` — set by the "+ New event" flow to mark this as a draft
  // being set up. We use it to hide Danger zone until the first save.
  const [createParams, setCreateParams] = useSearchParams();
  const back = useBackTarget({
    home: 'My Events',
    fallback: manageMode ? { path: '/leader/events', label: 'Manage events' } : { path: '/events', label: 'Events' },
  });
  // Never saved yet ("+ New event" → ?created=1): Cancel discards it.
  const justCreated = createParams.get('created') === '1';
  // Draft = saved but not published (hidden from members) — 2026-10-12.
  const isDraft = !!event?.draft;

  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [iconUrl, setIconUrl] = useState(null);
  const [iconBusy, setIconBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // design/026 — conversation set-up collected while the event is a draft;
  // saved together with everything else when the leader presses Create.
  const [convSetup, setConvSetup] = useState(EMPTY_CONVERSATION_SETUP);
  const [previewing, setPreviewing] = useState(false);
  const iconRef = useRef(null);

  // Seed / re-seed the editable snapshot whenever the loaded event changes.
  useEffect(() => {
    if (event) {
      setF(toForm(event));
      setIconUrl(event.eventIconUrl);
    }
  }, [event?.id, event?.updatedAt]);

  // Full-page spinner only on the first load. On reloads (after Save, icon
  // upload…) keep the page mounted so unsent text — e.g. a Conversation
  // draft — isn't thrown away.
  if ((loading && !event) || (event && !f)) return <div className="page"><Spinner /></div>;
  if (error) {
    const friendly = error.status === 403 ? "You don't have access to this event."
      : error.status === 404 ? 'This event no longer exists.' : null;
    return (
      <div className="page">
        <PageHead breadcrumb={<BackCrumb to={back} />} title={friendly ? 'Event unavailable' : 'Couldn’t load event'} />
        {friendly ? <Section><Empty>{friendly}</Empty></Section> : <ErrorNote error={error} onRetry={reload} />}
      </div>
    );
  }

  const r = event.request || {};
  const g = event.group || null;
  const state = (event.state || '').toLowerCase();
  const isOwner = !!(event.ownerUsername && me?.username && event.ownerUsername === me.username)
    || !!(me?.id && event.ownerId && event.ownerId === me.id);
  // Preview (from the editor) renders the learner view even for the owner.
  const canManage = !preview && manageMode && (isOwner || !!me?.isAdmin);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const dirty = canManage && f && JSON.stringify(f) !== JSON.stringify(toForm(event));

  const onIcon = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setIconBusy(true);
    try { const updated = await api.uploadEventIcon(id, file); setIconUrl(updated?.eventIconUrl || iconUrl); reload(); }
    catch (err) { alert(err.message); }
    finally { setIconBusy(false); }
  };

  const removeIcon = async () => {
    if (!window.confirm('Remove the icon image (revert to the color)?')) return;
    setIconBusy(true);
    try { await api.deleteEventIcon(id); setIconUrl(null); reload(); }
    catch (err) { alert(err.message); }
    finally { setIconBusy(false); }
  };

  // keepDraft: Preview while creating saves the form but keeps the draft
  // hidden; the Create / Save button publishes.
  // draft: true = hide (Unpublish), false = Publish, undefined = keep as is.
  // fromPreview: Preview's save — keeps the "new" marker so Cancel still discards.
  const save = async ({ draft, fromPreview = false } = {}) => {
    if (!f.requestName.trim()) return false;
    setBusy(true);
    const trim = (v) => (v && v.trim() ? v.trim() : null);
    // Text the user can empty: "" tells the gateway to clear it (null = leave as is).
    const clearable = (v) => (v || '').trim();
    try {
      await api.updateEvent(id, {
        requestName: f.requestName.trim(),
        requestTopic: clearable(f.requestTopic),
        requestDescription: clearable(f.requestDescription),
        date: trim(f.date),
        beginTime: trim(f.beginTime),
        endTime: trim(f.endTime),
        state: f.state,
        location: clearable(f.location),
        priceCents: Number(f.priceCents) || 0,
        isPrivate: !!f.isPrivate,
        coverColor: f.coverColor || '',
        iconColor: f.iconColor || '',
        draft,
      });
      // First save clears the ?created=1 draft marker so composer + danger
      // zone come back on subsequent visits.
      if (justCreated && !fromPreview) {
        // Create: also save the conversation set-up (settings + opening post).
        try {
          await applyConversationSetup('event', id, convSetup);
          setConvSetup(EMPTY_CONVERSATION_SETUP);
        } catch (e) {
          alert(`Event created, but the conversation set-up couldn't be saved: ${e?.data?.message || e.message}`);
        }
        const p = new URLSearchParams(createParams);
        p.delete('created');
        setCreateParams(p, { replace: true });
      }
      reload();
      return true;
    } catch (e) {
      if (!handleHasMembersError(e, 'events', id, navigate)) alert(e?.data?.message || e.message);
      return false;
    }
    finally { setBusy(false); }
  };
  const publish = () => save({ draft: false });
  const unpublish = () => {
    if (!window.confirm('Unpublish? It will be hidden from members until you publish it again.')) return;
    save({ draft: true });
  };

  // Preview: save first (so the preview shows current edits), then open the
  // learner view in a modal — nothing is registered or enrolled.
  const openPreview = async () => {
    if (dirty && !(await save({ fromPreview: true }))) return;
    setPreviewing(true);
  };

  // Cancel: a never-saved draft (?created=1) is discarded entirely so
  // "Untitled event" stubs don't pile up; otherwise unsaved edits are dropped.
  // Either way we return to wherever the user came from.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(justCreated ? '/leader/events' : `/event/${id}`);
  };
  const cancel = async () => {
    if (justCreated) {
      if (dirty && !window.confirm('Discard this new event?')) return;
      setCancelling(true);
      try { await api.deleteEvent(id); } catch (e) { alert(e.message); setCancelling(false); return; }
      // The draft no longer exists — go to the list (don't rely on history,
      // which could lead back to this deleted page).
      navigate(back.path, { replace: true });
      return;
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    goBack();
  };

  const del = async () => {
    // Warn with the registered count (owner excluded) so the owner knows who
    // loses access. Paid events get a refund reminder.
    const n = event.registeredCount || 0;
    // Decided 2026-10-02 — remove participants first (refunds happen there).
    if (!event.draft && blockDeleteWithMembers(n, 'event', 'events', id, navigate)) return;
    if (!window.confirm(`Delete "${f.requestName || r.name || 'this event'}"? This cannot be undone.`)) return;
    try { await api.deleteEvent(id); navigate(manageMode ? '/leader/events' : '/events'); }
    catch (e) { if (!handleHasMembersError(e, 'events', id, navigate)) alert(e?.data?.message || e.message); }
  };

  return (
    <div className="page">
      <PageHead
        breadcrumb={<BackCrumb to={back} />}
        title=""
        actions={
          <>
            {canManage && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <StatusPill draft={isDraft} />
                <button className="btn" disabled={busy || cancelling} onClick={cancel}>
                  {cancelling ? 'Cancelling…' : 'Cancel'}
                </button>
                <button className="btn" disabled={busy || cancelling || !f.requestName.trim()} onClick={() => save()}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn" disabled={busy || cancelling || !f.requestName.trim()} onClick={openPreview}>Preview</button>
                {isDraft
                  ? <button className="btn btn-primary" disabled={busy || cancelling || !f.requestName.trim()} onClick={publish}>Publish</button>
                  : <button className="btn" disabled={busy || cancelling} onClick={unpublish}>Unpublish</button>}
              </div>
            )}
            {!canManage && (isOwner || me?.isAdmin) && !preview && (
              <button className="btn btn-primary" onClick={() => navigate(`/leader/event/${id}`, fromHere(`/event/${id}`, (r.name || 'Event')))}>Manage event →</button>
            )}
            {/* design/024 — Event chat retired. Register + email notifications
                are the channel; no in-app chat surface. */}
          </>
        }
      />

      <ProgramHero kind="Event" name={canManage ? (f.requestName || '(unnamed event)') : (r.name || '(unnamed event)')}
        iconSrc={api.iconUrl(iconUrl || event.eventIconUrl)} coverColor={canManage ? f.coverColor : event.coverColor}
        iconColor={canManage ? f.iconColor : event.iconColor}
        sub={[g?.name, event.ownerUsername ? `by @${event.ownerUsername}` : null, state].filter(Boolean).join(' · ')} />

      {/* Registration panel (design/024). Shown to everyone except the
          owner — including members, per the design (server dedupes and
          returns alreadyMember=true so re-clicks are safe). */}
      {(!event.isOwner || preview) && (
        <RegisterPanel event={event} fromHome={fromHome} purchase={purchase}
          isRegistered={!preview && !!event.isParticipant} />
      )}

      <div className="grid-2">
        <div>
          <Section title={canManage ? 'Event details' : null}>
            {canManage ? (
              <>
                <div className="form-field"><label>Cover color</label>
                  <ColorField value={f.coverColor} onChange={(hex) => setF((p) => ({ ...p, coverColor: hex }))} />
                </div>
                <IconColorField value={f.iconColor} coverValue={f.coverColor}
                  onChange={(v) => setF((p) => ({ ...p, iconColor: v }))} />
                <div className="form-field">
                  <label>Icon image (optional — shown instead of the icon color)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar name={f.requestName} colorSeed={id} src={api.iconUrl(iconUrl)} square size={48}
                      bg={(f.iconColor || f.coverColor) ? cssBackground(f.iconColor || f.coverColor) : undefined} />
                    <input ref={iconRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onIcon} />
                    <button type="button" className="btn btn-sm" disabled={iconBusy} onClick={() => iconRef.current?.click()}>
                      {iconBusy ? 'Uploading…' : (iconUrl ? 'Replace image' : 'Upload image')}
                    </button>
                    {iconUrl && (
                      <button type="button" className="btn btn-sm btn-danger" disabled={iconBusy} onClick={removeIcon}>Remove</button>
                    )}
                  </div>
                </div>

                <EditField label="Name" required value={f.requestName} onChange={set('requestName')} />
                <EditField label="Topic" value={f.requestTopic} onChange={set('requestTopic')} />
                <EditField label="Description" textarea value={f.requestDescription} onChange={set('requestDescription')} />
                <EditField label="Location" value={f.location} onChange={set('location')} placeholder="address, zoom link, etc." />
                <EditField label="Date" type="date" value={f.date} onChange={set('date')} />
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}><EditField label="Begin (CST)" type="time" value={f.beginTime} onChange={set('beginTime')} /></div>
                  <div style={{ flex: 1 }}><EditField label="End (CST)" type="time" value={f.endTime} onChange={set('endTime')} /></div>
                </div>
                <div className="form-field">
                  <label>State</label>
                  <select className="input" value={f.state} onChange={set('state')}>
                    <option value="STARTED">Upcoming</option>
                    <option value="ONGOING">Ongoing</option>
                    <option value="COMPLETE">Complete</option>
                  </select>
                </div>
                <PriceField cents={f.priceCents}
                  onChangeCents={(v) => setF((p) => ({ ...p, priceCents: v }))}
                  paidHint="Paid — tickets bought via Stripe on the web."
                  freeHint="$0 = free (register only)." />
                <AnnounceToggle kind="event" id={id} isPrivate={!!f.isPrivate} />
                <div className="form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!f.isPrivate}
                      onChange={(e) => setF((p) => ({ ...p, isPrivate: e.target.checked }))} />
                    <span>Private event</span>
                  </label>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Hidden from the public catalog. Only the owner and invited participants can see it.</div>
                </div>
              </>
            ) : (
              <>
                <ReadField label="Topic" value={r.topic} />
                <ReadField label="Description" value={r.description} />
                <ReadField label="Location"
                  value={preview
                    ? ((event.location || event.meetingLink) ? '🔒 Shared after you register' : null)
                    : (event.location || (event.detailsHidden ? '🔒 Shared after you register' : null))} />
                <ReadField label="Date" value={formatDateUS(event.date)} />
                <ReadField label="Begin (CST)" value={formatTime12h(event.beginTime)} />
                <ReadField label="End (CST)" value={formatTime12h(event.endTime)} />
                <ReadField label="State" value={stateLabel(event.state)} />
                {/* 2026-10-16 — registered people see "You're registered" instead of the price. */}
                {!preview && event.isParticipant && !event.isOwner ? (
                  <div style={{ marginTop: 8 }}>
                    <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)', fontWeight: 700 }}>✓ You're registered</span>
                  </div>
                ) : event.priceCents > 0 && (
                  <ReadField label="Ticket" value={`$${(event.priceCents / 100).toFixed(2)}`} />
                )}
                {event.isPrivate && <ReadField label="Visibility" value="🔒 Private (invite-only)" />}
              </>
            )}
          </Section>

          {canManage && <AssociationTabs sourceKind="event" sourceId={id} />}
        </div>

        <div>
          {g && (
            <Section title="Group">
              <a onClick={() => navigate(`/group/${g.id}`)} style={{ cursor: 'pointer', fontWeight: 600 }}>⬣ {g.name}</a>
            </Section>
          )}
          {/* design/024 — Files section fully retired for Events (both
              read-only and manage views). Backend endpoints kept for now. */}

          {/* Hide Danger zone on freshly-created drafts (?created=1 in URL).
              Cleared by the first successful Save. */}
          {canManage && !justCreated && (
            <Section>
              <div className="danger-zone">
                <div className="label">Danger zone</div>
                <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }} onClick={del}>
                  Delete event
                </button>
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* design/026 — members-only conversation (teaser for everyone else). */}
      {!preview && canManage && justCreated && (
        <ConversationSetup value={convSetup} onChange={setConvSetup} />
      )}
      {!preview && !(canManage && justCreated) && (
        <Conversation kind="event" id={id} key={`conv-${!!event.isParticipant}`}
          onRegister={() => navigate(`/event/${id}/register`, { state: fromHome ? { from: 'home' } : undefined })} />
      )}

      <RelatedStrips sourceKind="event" sourceId={id} />

      {previewing && (
        <PreviewModal kind="event" onClose={() => setPreviewing(false)}>
          <EventDetailPage preview />
        </PreviewModal>
      )}
    </div>
  );
}

/**
 * Registration panel (design/024). Single "Register" CTA that routes to the
 * wizard for both free and paid events. Also renders the return banners for:
 *   ?registered=1     → freshly joined a free event (or already a member).
 *   ?checkout=success → Stripe redirected back after paid registration.
 *   ?checkout=cancel  → Stripe checkout cancelled.
 */
function RegisterPanel({ event, fromHome, isRegistered = false, purchase }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('checkout');
  const registered = params.get('registered') === '1';
  const paid = (event.priceCents || 0) > 0;
  const price = paid ? `$${(event.priceCents / 100).toFixed(2)}` : 'Free';

  const dismiss = () => {
    const p = new URLSearchParams(params);
    p.delete('checkout'); p.delete('session_id'); p.delete('registered');
    setParams(p, { replace: true });
  };

  const go = () => navigate(`/event/${event.id}/register`, { state: fromHome ? { from: 'home' } : undefined });

  return (
    <>
      {((status === 'success' && (purchase?.confirming || purchase?.failed || isRegistered)) || (registered && isRegistered)) && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}>
            {status === 'success' && purchase?.confirming ? <strong>Confirming your payment…</strong>
              : status === 'success' && purchase?.failed && !isRegistered
                ? <><strong>Payment received.</strong> Your registration is still being confirmed — refresh this page in a minute.</>
                : <><strong>You're registered.</strong>{paid ? ' Payment received.' : ''}</>}
          </div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {status === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {/* Registered → nothing here (decided 2026-10-02: no permanent badge). */}
      {isRegistered ? null : (
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {paid ? `${price} — one-time` : 'Free event'}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              {paid
                ? 'Register to reserve your ticket and get event updates.'
                : 'Register to get event updates and reminders.'}
            </div>
          </div>
          <button className="btn btn-primary" onClick={go}>Register →</button>
        </div>
      </Section>
      )}
    </>
  );
}

/**
 * @deprecated Superseded by RegisterPanel (design/024). Kept only for
 * reference until the mobile app cuts over.
 */
function FreeAttendPanel({ event, onJoined }) {
  const [joining, setJoining] = useState(false);
  const join = async () => {
    setJoining(true);
    try {
      await api.joinEvent(event.id);
      onJoined?.();
    } catch (e) { alert(e.message); setJoining(false); }
  };
  return (
    <Section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Free event</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Attend to join the chat and get event notifications.</div>
        </div>
        <button className="btn btn-primary" disabled={joining} onClick={join}>
          {joining ? 'Joining…' : 'Attend'}
        </button>
      </div>
    </Section>
  );
}

function TicketPanel({ event }) {
  const [params, setParams] = useSearchParams();
  const [buying, setBuying] = useState(false);
  const status = params.get('checkout');

  const buy = async () => {
    setBuying(true);
    try {
      const { url } = await api.eventCheckout(event.id);
      if (url) window.location.assign(url);
    } catch (e) { alert(e.message); setBuying(false); }
  };
  const dismiss = () => {
    const p = new URLSearchParams(params); p.delete('checkout'); p.delete('session_id');
    setParams(p, { replace: true });
  };
  const price = `$${(event.priceCents / 100).toFixed(2)}`;

  return (
    <>
      {status === 'success' && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}><strong>Ticket confirmed.</strong> Payment received.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {status === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{price} — one-time</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Buy a ticket to attend this event.</div>
          </div>
          <button className="btn btn-primary" disabled={buying} onClick={buy}>
            {buying ? 'Redirecting…' : `Buy ticket — ${price}`}
          </button>
        </div>
      </Section>
    </>
  );
}
